// src/AppLayout.tsx

import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  IconButton,
  AppBar,
  Toolbar,
  CssBaseline,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import BarChartIcon from '@mui/icons-material/BarChart';
import UpdateIcon from '@mui/icons-material/Update';
import LogoutIcon from '@mui/icons-material/Logout';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import InsightsIcon from '@mui/icons-material/Insights';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import SummarizeIcon from '@mui/icons-material/Summarize';
import DescriptionIcon from '@mui/icons-material/Description';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import { useThemeContext } from './components/theme/ThemeContext';
import { Dashboard, Engineering, FindInPage, Notifications as NotificationsIcon } from '@mui/icons-material';
import NotificacaoIcone from './components/NotificacaoIcone';
import { NotificacaoProvider } from './context/NotificacaoContext';
import { useAuth } from './context/AuthContext';

interface AppLayoutProps {
  isAuthenticated: boolean;
}

const drawerWidth = 240;

const AppLayout: React.FC<AppLayoutProps> = ({ isAuthenticated }) => {
  const [open, setOpen] = useState(true);
  const { toggleTheme, mode } = useThemeContext();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const handleLogout = () => {
    signOut();
  };

  if (!isAuthenticated) return <Outlet />;

  const activeStyle = (path: string) => {
    const isActive = location.pathname === path;

    return {
      backgroundColor: isActive ? theme.palette.primary.main : 'inherit',
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
        opacity: isActive ? 1 : 0.6,
        '& .MuiTypography-root': {
          color: `${theme.palette.primary.contrastText} !important`,
          fontWeight: 500,
        },
      },
      '& .MuiTypography-root': {
        color: isActive
          ? `${theme.palette.primary.contrastText} !important`
          : `${theme.palette.text.primary} !important`,
        fontWeight: isActive ? 500 : 400,
      }
    };
  };

  const activeStyleLogout = (path: string) => ({
    backgroundColor: location.pathname === path ? theme.palette.primary.main : 'inherit',
    '&:hover': {
      backgroundColor: theme.palette.secondary.main,
      opacity: location.pathname === path ? 1 : 0.4,
      '& .MuiTypography-root': {
        color: theme.palette.primary.contrastText,
        fontWeight: 500,
      },
      '& .MuiListItemIcon-root svg': {
        color: '#fff',
      }
    },
    '& .MuiTypography-root': {
      color: location.pathname === path ? theme.palette.primary.contrastText : 'inherit',
      fontWeight: location.pathname === path ? 500 : 400,
    },
    '& .MuiListItemIcon-root svg': {
      color: location.pathname === path ? theme.palette.primary.contrastText : theme.palette.text.primary,
    }
  });

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      {/* TopBar com tema e ajuste de posição responsivo */}
      <AppBar
        position="fixed"
        sx={{
          width: open ? `calc(100% - ${drawerWidth}px)` : '100%',
          ml: open ? `${drawerWidth}px` : 0,
          transition: 'width 0.3s, margin-left 0.3s',
          borderRadius: '0'
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={() => setOpen(!open)}
            sx={{ mr: 2 }}
          >
            {open ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
          <img src='/logo512teste.png' width="50px" alt="Logo Think Way" />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, ml: '20px' }}>
            Think Way
          </Typography>

          {/* Ícone de notificações */}
          <NotificacaoIcone />

          <IconButton color="inherit" onClick={toggleTheme}>
            {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        open={open}
        sx={{
          width: open ? drawerWidth : 60,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: open ? drawerWidth : 60,
            boxSizing: 'border-box',
            transition: 'width 0.3s',
            borderRadius: '0'
          },
        }}
      >
        {open && (
          <Typography variant="h6" component="div" sx={{ m: 2 }}>
            Menu
          </Typography>
        )}
        <List sx={{ flexGrow: 1 }}>
          <ListItem onClick={() => setOpen(!open)} sx={{ textDecoration: 'none', display: open ? 'none' : 'block' }} >
            <ListItemIcon>
              <MenuIcon />
            </ListItemIcon>
          </ListItem>

          <ListItem component={Link} to="/tw/dashboard" sx={activeStyle('/tw/dashboard')}>
            <ListItemIcon>
              <Dashboard color='secondary' />
            </ListItemIcon>
            <ListItemText secondary="Dashboard" sx={{ textDecoration: 'none', display: open ? 'block' : 'none' }} />
          </ListItem>

          <ListItem component={Link} to="/tw/diretrizes" sx={activeStyle('/tw/diretrizes')}>
            <ListItemIcon>
              <FactCheckIcon color="secondary" />
            </ListItemIcon>
            <ListItemText secondary="Diretrizes" sx={{ textDecoration: 'none', display: open ? 'block' : 'none' }} />
          </ListItem>

          <ListItem component={Link} to="/tw/colaboradores" sx={activeStyle('/tw/colaboradores')}>
            <ListItemIcon>
              <Engineering color="secondary" />
            </ListItemIcon>
            <ListItemText secondary="Colaboradores" sx={{ textDecoration: 'none', display: open ? 'block' : 'none' }} />
          </ListItem>

          <ListItem component={Link} to="/tw/metas-globais" sx={activeStyle('/tw/metas-globais')}>
            <ListItemIcon>
              <UpdateIcon color='secondary' />
            </ListItemIcon>
            <ListItemText secondary="Metas Globais" sx={{ textDecoration: 'none', display: open ? 'block' : 'none' }} />
          </ListItem>

          <ListItem component={Link} to="/tw/ambiente-interno" sx={activeStyle('/tw/ambiente-interno')}>
            <ListItemIcon>
              <TroubleshootIcon color='secondary' />
            </ListItemIcon>
            <ListItemText secondary="Ambiente Interno" sx={{ textDecoration: 'none', display: open ? 'block' : 'none' }} />
          </ListItem>

          <ListItem component={Link} to="/tw/ambiente-externo" sx={activeStyle('/tw/ambiente-externo')}>
            <ListItemIcon>
              <DataUsageIcon color='secondary' />
            </ListItemIcon>
            <ListItemText secondary="Ambiente Externo" sx={{ textDecoration: 'none', display: open ? 'block' : 'none' }} />
          </ListItem>


          <ListItem component={Link} to="/tw/swot" sx={activeStyle('/tw/swot')}>
            <ListItemIcon>
              <FindInPage color='secondary' />
            </ListItemIcon>
            <ListItemText secondary="Swot" sx={{ textDecoration: 'none', display: open ? 'block' : 'none' }} />
          </ListItem>

          <ListItem component={Link} to="/tw/plano-de-acao" sx={activeStyle('/tw/plano-de-acao')}>
            <ListItemIcon>
              <AssignmentTurnedInIcon color='secondary' />
            </ListItemIcon>
            <ListItemText secondary="Plano de Ação" sx={{ textDecoration: 'none', display: open ? 'block' : 'none' }} />
          </ListItem>

          <ListItem component={Link} to="/tw/desdobramento" sx={activeStyle('/tw/desdobramento')}>
            <ListItemIcon>
              <InsightsIcon color='secondary' />
            </ListItemIcon>
            <ListItemText secondary="Tarefas" sx={{ textDecoration: 'none', display: open ? 'block' : 'none' }} />
          </ListItem>

          {/* <ListItem component={Link} to="/tw/notificacoes" sx={activeStyle('/tw/notificacoes')}>
            <ListItemIcon>
              <NotificationsIcon color='secondary' />
            </ListItemIcon>
            <ListItemText secondary="Notificações" sx={{ textDecoration: 'none', display: open ? 'block' : 'none' }} />
          </ListItem> */}

          {/* <ListItem component={Link} to="/tw/avaliacao" sx={activeStyle('/tw/avaliacao')}>
            <ListItemIcon>
              <BarChartIcon color="secondary" />
            </ListItemIcon>
            <ListItemText secondary="Avaliação" sx={{ textDecoration: 'none', display: open ? 'block' : 'none' }} />
          </ListItem> */}

          {/* <ListItem component={Link} to="/tw/relatorios" sx={activeStyle('/tw/relatorios')}>
            <ListItemIcon>
              <DescriptionIcon color='secondary' />
            </ListItemIcon>
            <ListItemText secondary="Relatórios" sx={{ textDecoration: 'none', display: open ? 'block' : 'none' }} />
          </ListItem> */}
        </List>
        <Box sx={{ mt: 'auto' }}>
          <List>
            <ListItem component={Link} onClick={handleLogout} to="/tw/login" sx={activeStyleLogout('/tw/login')}>
              <ListItemIcon>
                <LogoutIcon color='secondary' />
              </ListItemIcon>
              <ListItemText secondary="Logout" sx={{ textDecoration: 'none', display: open ? 'block' : 'none' }} />
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Conteúdo principal */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 4,
          mt: '64px',
          transition: 'margin-left 0.3s',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default AppLayout;